import { useEffect, useRef } from 'react';
import { useAiRunStore } from '../store/aiRunStore';
import { useAuthStore } from '../store/authStore';
import { pollJobStatus } from '../services/aiRunService';

const POLL_INTERVAL_MS = 3000;
const MAX_CONSECUTIVE_FAILURES = 10;

/**
 * Background poller that keeps track of an in-progress detector job
 * even when the user has navigated away from DetectorPage.
 *
 * Call this hook from AppLayout so it runs globally while authenticated.
 */
export function useDetectorJobPoller() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeDetectorJob = useAiRunStore((s) => s.activeDetectorJob);
  const updateActiveDetectorJob = useAiRunStore((s) => s.updateActiveDetectorJob);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const jobId = activeDetectorJob?.jobId;
  const status = activeDetectorJob?.status;

  useEffect(() => {
    // Only poll while logged in and there's an active job still in progress.
    // The auth gate matters on its own: AppLayout must call this hook
    // unconditionally (Rules of Hooks) before its isAuthenticated check, so
    // without this the poller would keep hitting the job-status endpoint
    // with a cleared token right after logout.
    if (!isAuthenticated || !jobId || status === 'done' || status === 'error') {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    let consecutiveFailures = 0;

    const poll = async () => {
      try {
        const data = await pollJobStatus(jobId);
        consecutiveFailures = 0;
        updateActiveDetectorJob({
          status: data.status,
          progress: data.progress,
          stage: data.stage,
          message: data.message,
        });
        // Clear interval when terminal state is reached
        if (data.status === 'done' || data.status === 'error') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch {
        consecutiveFailures += 1;
        // Give up after repeated failures instead of polling forever — a
        // deleted job record or persistent server error would otherwise
        // leave the Header spinner stuck on "Analyzing…" indefinitely with
        // no way for the user to dismiss it.
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          updateActiveDetectorJob({
            status: 'error',
            message: 'Lost connection to this analysis job.',
          });
        }
      }
    };

    // Poll immediately, then on interval
    void poll();
    intervalRef.current = setInterval(() => { void poll(); }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, jobId, status, updateActiveDetectorJob]);
}
